import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import RapidCaptureParser from "../lib/rapid_capture_parser"

const TOKEN_CLASSES = {
  youtrack: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded px-0.5',
  due: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded px-0.5',
  project_valid: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded px-0.5',
  assignee_valid: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded px-0.5',
  plain_date: 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full px-1.5 py-0.5 text-[11px]'
}

export default class extends Controller {
  static targets = ["inlineForm", "inlineInput"]
  static values = {
    projects: Array,
    users: Array,
    youtrackBase: String
  }

  connect() {
    this.parser = new RapidCaptureParser({
      projects: this.projectsValue,
      users: this.usersValue
    })
    this._debounceTimer = null
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
  }

  addToQuadrant(event) {
    event.preventDefault()
    const quadrant = event.currentTarget.dataset.quadrant

    // Hide any other open inline forms
    this.inlineFormTargets.forEach(form => form.classList.add("hidden"))

    // Show the inline form for this quadrant
    const form = this.inlineFormTargets.find(f => f.dataset.inlineQuadrant === quadrant)
    if (form) {
      form.classList.remove("hidden")
      const input = this.inlineInputTargets.find(i => i.dataset.inlineQuadrant === quadrant)
      if (input) {
        input.textContent = ""
        input.focus()
        input.oninput = () => this._onInput(input)
        input.onkeydown = (e) => this._onKeydown(e, quadrant, input, form)
        input.onblur = () => this._onBlur(input, form)
      }
    }
  }

  _onInput(input) {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => {
      this._processHighlighting(input)
    }, 150)
  }

  _processHighlighting(input) {
    try {
      const text = input.textContent
      const parsed = this.parser.parse(text)

      if (parsed.tokens.length > 0) {
        const html = this._buildHighlightedHTML(text, parsed.tokens)
        const offset = this._saveCursorOffset(input)
        input.innerHTML = html
        this._restoreCursorOffset(input, offset)
      }
    } catch (e) {
      // silently catch parse/render errors
    }
  }

  _onKeydown(event, quadrant, input, form) {
    if (event.key === "Enter") {
      event.preventDefault()
      const raw = input.textContent.trim()
      if (!raw) return

      const parsed = this.parser.parse(raw)
      const base = this.youtrackBaseValue || ""

      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      const formData = new FormData()
      formData.append("command_centre_task[title]", parsed.title)
      formData.append("command_centre_task[quadrant]", quadrant)

      if (parsed.fields.project_tag) {
        formData.append("command_centre_task[project_tag]", parsed.fields.project_tag)
      }
      if (parsed.fields.assignee_id !== null && parsed.fields.assignee_id !== undefined) {
        formData.append("command_centre_task[assignee_id]", parsed.fields.assignee_id)
      }
      if (parsed.fields.due_date) {
        formData.append("command_centre_task[due_date]", parsed.fields.due_date)
      }
      if (parsed.fields.youtrack_url) {
        formData.append("command_centre_task[youtrack_url]", `${base}/issue/${parsed.fields.youtrack_url}`)
      }

      const emptyState = document.getElementById(`empty_${quadrant}`)
      if (emptyState) emptyState.remove()

      fetch("/command_centre/tasks", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: formData
      }).then(response => {
        if (response.ok) return response.text()
      }).then(html => {
        if (html) Turbo.renderStreamMessage(html)
      })

      input.textContent = ""
      form.classList.add("hidden")
    }

    if (event.key === "Escape") {
      input.textContent = ""
      form.classList.add("hidden")
    }
  }

  _onBlur(input, form) {
    setTimeout(() => {
      if (!input.textContent.trim()) {
        form.classList.add("hidden")
      }
    }, 150)
  }

  // --- Highlighting helpers (same logic as rapid_capture_controller) ---

  _buildHighlightedHTML(text, tokens) {
    let html = ''
    let cursor = 0

    for (const token of tokens) {
      if (token.start > cursor) {
        html += this._escapeHTML(text.slice(cursor, token.start))
      }

      const classes = this._classesForToken(token)
      if (classes) {
        html += `<span class="${classes}">${this._escapeHTML(token.raw)}</span>`
      } else {
        html += this._escapeHTML(token.raw)
      }

      cursor = token.end
    }

    if (cursor < text.length) {
      html += this._escapeHTML(text.slice(cursor))
    }

    return html
  }

  _classesForToken(token) {
    switch (token.type) {
      case 'youtrack': return TOKEN_CLASSES.youtrack
      case 'due': return TOKEN_CLASSES.due
      case 'project': return token.valid ? TOKEN_CLASSES.project_valid : null
      case 'assignee': return token.valid ? TOKEN_CLASSES.assignee_valid : null
      case 'plain_date': return TOKEN_CLASSES.plain_date
      default: return null
    }
  }

  _escapeHTML(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  _saveCursorOffset(el) {
    const sel = window.getSelection()
    if (!sel || !sel.rangeCount) return 0
    const range = sel.getRangeAt(0)
    const preRange = range.cloneRange()
    preRange.selectNodeContents(el)
    preRange.setEnd(range.startContainer, range.startOffset)
    return preRange.toString().length
  }

  _restoreCursorOffset(el, offset) {
    const sel = window.getSelection()
    if (!sel) return
    const range = document.createRange()
    let currentOffset = 0
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
    let node
    while ((node = walker.nextNode())) {
      if (currentOffset + node.length >= offset) {
        range.setStart(node, offset - currentOffset)
        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)
        return
      }
      currentOffset += node.length
    }
    range.selectNodeContents(el)
    range.collapse(false)
    sel.removeAllRanges()
    sel.addRange(range)
  }
}
